import store from '../store';
import { ACTIONS } from './actions';
import * as Selectors from '../selectors';
import * as NetworkActions from './network.actions';
import * as SourceCardUtil from '../../components/availableSources/SourceCardUtil';
import * as TargetCardUtil from '../../components/targets/TargetCardUtil';
import * as ConnectorCardUtil from '../../components/availableConnectorsV2/ConnectorCardUtil';
import * as PopupAlertManager from '../../components/popupAlerts/index';
import * as API from '../../api/client';
import isEmptyObject from '../../utils/isEmptyObject';

export async function initializeWidget(api_key, env_id, api_url, options) {
  store.dispatch({
    type: ACTIONS.INITIALIZE_WIDGET,
    payload: { api_key, env_id, api_url },
  });

  // Process options if provided
  handleOptions(options);

  store.dispatch(NetworkActions.fetchPreferences());
  store.dispatch(NetworkActions.fetchFeatureFlags());
  await store.dispatch(NetworkActions.fetchSupportedFlows());
  await NetworkActions.fetchAllSupportedSources();
  await NetworkActions.fetchAllSupportedTargets();
  await NetworkActions.fetchAllSupportedConnectors();
}

export function setIsConnection(value) {
  store.dispatch({
    type: ACTIONS.SET_IS_CONNECTION,
    payload: value,
  });
}

export function setTenant(tenantId) {
  return {
    type: ACTIONS.SET_TENANT,
    payload: tenantId,
  };
}

export function openWidget(tenant_id) {
  if (/\s/.test(tenant_id)) {
    throw new Error('hotglue tenant ids cannot have spaces!');
  } else {
    store.dispatch({
      type: ACTIONS.SET_TENANT,
      payload: tenant_id,
    });

    setOpenWidget();
  }
}

export function closeWidget() {
  // Alert listener if exists
  const listener = Selectors.getListener();
  if (listener && listener.onWidgetClose) {
    listener.onWidgetClose();
  }

  PopupAlertManager.clearModal();

  store.dispatch(setDialogSize(400, 600));

  store.dispatch({
    type: ACTIONS.CLOSE_WIDGET,
  });

  store.dispatch({
    type: ACTIONS.SELECT_ENTITY,
    payload: {},
  });

  store.dispatch({
    type: ACTIONS.SELECT_FLOW,
    payload: undefined,
  });
}


export function goNextStepOrBack(entity) {
  const { nextStep } = store.getState().configuration;
  if (!nextStep) {
    goBack();
    return;
  }

  goNextStep(entity, nextStep);
}

export function goNextStep(entity, nextStep) {
  store.dispatch({
    type: ACTIONS.SELECT_ENTITY,
    payload: entity,
  });
  if (nextStep === 'fieldMap')
    editFieldMap(entity)
  if (nextStep === 'mapping')
    goToEntityConfig(entity, entity.entityType)
}

export function goBack() {
  const { breadcrumbs, page } = store.getState().navigation;

  if (breadcrumbs && breadcrumbs.length > 1) {
    breadcrumbs[breadcrumbs.length - 2].onClick();
    if (page === 'FieldMapTransferList') return;
  }

  store.dispatch({
    type: ACTIONS.SELECT_SOURCE,
    payload: undefined,
  });

  store.dispatch({
    type: ACTIONS.SELECT_TARGET,
    payload: undefined,
  });

  const entity = Selectors.getSelectedEntity();

  if (entity) {
    store.dispatch({
      type: ACTIONS.SELECT_ENTITY,
      payload: {
        ...entity,
        label: entity?.label?.split(' ')?.[0] ?? '',
      },
    });
  }
}

export function showLoading() {
  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'Loading',
      title: `Loading`,
    },
  });
}

export function doCorsCheck() {
  store.dispatch({
    type: ACTIONS.SET_CORS_CHECK,
    payload: true,
  });
}

export function backToFlowSources(flowId) {
  const targetFlow = Selectors.supportedFlow(flowId);

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'FlowSources',
      title: `Where is your ${targetFlow.name} data?`,
      breadcrumbs: [
        {
          label: 'Flows',
          onClick: backToFlows,
        },
        targetFlow.name,
      ],
    },
  });

  store.dispatch({
    type: ACTIONS.CLEAR_FIELD_MAP_SELECTION,
  });
}
export function clearEntity() {
  store.dispatch({ type: ACTIONS.SELECT_ENTITY, payload: {} });
}

export function clickFlow(flowId) {
  const targetFlow = Selectors.supportedFlow(flowId);

  if(!targetFlow)
    throw new Error(`There is not a flow with the id ${flowId}`)

  store.dispatch({
    type: ACTIONS.SELECT_FLOW,
    payload: flowId,
  });

  if (targetFlow?.version === 2) {
    store.dispatch({ type: ACTIONS.CLEAR_CONNECTORS });

    store.dispatch({
      type: ACTIONS.CHANGE_PAGE,
      payload: {
        page: 'FlowV2Connectors',
        title: `Where should your ${targetFlow.name} data go?`,
        breadcrumbs: [
          {
            label: 'Flows',
            onClick: backToFlows,
          },
          targetFlow.name,
        ],
      },
    });
  } else {
    if (targetFlow.type) {
      // Re-fetch targets
      store.dispatch({ type: ACTIONS.CLEAR_TARGETS });

      store.dispatch({
        type: ACTIONS.CHANGE_PAGE,
        payload: {
          page: 'FlowTargets',
          title: `Where should your ${targetFlow.name} data go?`,
          breadcrumbs: [
            {
              label: 'Flows',
              onClick: backToFlows,
            },
            targetFlow.name,
          ],
        },
      });
    } else {
      // Re-fetch sources
      store.dispatch({ type: ACTIONS.CLEAR_SOURCES });
      store.dispatch({
        type: ACTIONS.CHANGE_PAGE,
        payload: {
          page: 'FlowSources',
          title: `Where is your ${targetFlow.name} data?`,
          breadcrumbs: [
            {
              label: 'Flows',
              onClick: backToFlows,
            },
            targetFlow.name,
          ],
        },
      });
    }
  }
}

export function selectSFTPFileSource(source) {
  const { flows } = store.getState();
  const targetFlow = Selectors.supportedFlow(flows.selectedFlow);
  store.dispatch({
    type: ACTIONS.SELECT_SOURCE,
    payload: source,
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'SelectSFTPFile',
      title: `SFTP`,
      breadcrumbs: [
        {
          label: 'Flows',
          onClick: backToFlows,
        },
        {
          label: targetFlow.name,
          onClick: () => clickFlow(flows.selectedFlow),
        },
        'SFTP',
      ],
    },
  });
}

export function selectFileSource(source) {
  const { flows } = store.getState();
  const targetFlow = Selectors.supportedFlow(flows.selectedFlow);
  store.dispatch({
    type: ACTIONS.SELECT_SOURCE,
    payload: source,
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'UploadFile',
      title: `Upload files to ${targetFlow.name}`,
      breadcrumbs: [
        {
          label: 'Flows',
          onClick: backToFlows,
        },
        {
          label: targetFlow.name,
          onClick: () => clickFlow(flows.selectedFlow),
        },
        'Upload Files',
      ],
    },
  });
}

export function backToFieldMapSelector(isFlowV2 = false) {
  const { flows } = store.getState();
  const entity = Selectors.getSelectedEntity();
  const targetFlow = Selectors.supportedFlow(flows.selectedFlow);

  store.dispatch({
    type: isFlowV2 ? ACTIONS.SELECT_STREAM_CONNECTOR : ACTIONS.SELECT_STREAM,
    payload: undefined,
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: isFlowV2 ? 'FieldMapSelectorV2' : 'FieldMapSelector',
      title: 'Select Fields',
      breadcrumbs: [
        {
          label: 'Flow',
          onClick: backToFlows,
        },
        {
          label: targetFlow.name,
          onClick: () => backToFlowSources(flows.selectedFlow),
        },
        {
          label: entity.label,
          onClick: () => goToEntityConfig(entity, entity.id ? 'connector' : 'source'),
        },
        `${entity.label} Field Map`,
      ],
    },
  });
}

export function setFieldMapSelection(fieldMap, stream = undefined, isFlowV2) {
  store.dispatch({
    type: isFlowV2 ? ACTIONS.SET_FIELD_MAP_SELECTION_CONNECTOR : ACTIONS.SET_FIELD_MAP_SELECTION,
    payload: fieldMap,
    params: {
      stream,
    },
  });
}

export function clearFieldMapSelection(isFlowV2) {
  store.dispatch({
    type: isFlowV2 ? ACTIONS.CLEAR_FIELD_MAP_SELECTION_CONNECTOR : ACTIONS.CLEAR_FIELD_MAP_SELECTION,
  });
}

export function selectStream(stream, isFlowV2 = false, primaryKeys = []) {
  const { flows } = store.getState();
  const targetFlow = Selectors.supportedFlow(flows.selectedFlow);
  const entity = Selectors.getSelectedEntity();

  store.dispatch({
    type: isFlowV2 ? ACTIONS.SET_CONNECTOR_PRIMARY_KEYS : ACTIONS.SET_SOURCE_PRIMARY_KEYS,
    payload: primaryKeys,
  });

  store.dispatch({
    type: isFlowV2 ? ACTIONS.SELECT_STREAM_CONNECTOR : ACTIONS.SELECT_STREAM,
    payload: stream,
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'FieldMapTransferList',
      title: `${entity.label} Field Map`,
      breadcrumbs: [
        {
          label: 'Flow',
          onClick: backToFlows,
        },
        {
          label: targetFlow.name,
          onClick: () => {
            backToFlowSources(flows.selectedFlow);
          },
        },
        {
          label: `Configure ${entity.label}`,
          onClick: () => goToEntityConfig(entity),
        },
        {
          label: `${entity.label} Field Map`,
          onClick: () => backToFieldMapSelector(isFlowV2),
        },
        `${stream}`,
      ],
    },
  });
}

export function setFieldMap(entityId, fieldMap, stream, isFlowV2) {
  store.dispatch({
    type: isFlowV2 ? ACTIONS.SET_FIELD_MAP_CONNECTOR : ACTIONS.SET_FIELD_MAP,
    payload: fieldMap,
    params: {
      [isFlowV2 ? 'id' : 'tap']: entityId,
      stream,
    },
  });
}

export function editFieldMap(entity) {
  const { flows } = store.getState();
  const targetFlow = Selectors.supportedFlow(flows.selectedFlow);

  store.dispatch({
    type: entity.entityType === 'connector' ? ACTIONS.SELECT_CONNECTOR : ACTIONS.SELECT_SOURCE,
    payload: entity,
  });

  store.dispatch({
    type: ACTIONS.SET_DIALOG_SIZE,
    payload: { width: '583px', height: '686px' },
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: entity.entityType === 'connector' ? 'FieldMapSelectorV2' : 'FieldMapSelector',
      title: `${entity.label} Field Map`,
      breadcrumbs: [
        {
          label: 'Flow',
          onClick: backToFlows,
        },
        {
          label: targetFlow.name,
          onClick: () => {
            clickFlow(flows.selectedFlow);
            store.dispatch({
              type: ACTIONS.SET_DIALOG_SIZE,
              payload: { width: '400px', height: '600px' },
            });
          },
        },
        {
          label: entity.label,
          onClick: () => goToEntityConfig(entity),
        },
        `${entity.label} Field Map`,
      ],
    },
  });
}

export function selectEntity(entity, entityType = 'source') {
  store.dispatch({
    type: ACTIONS.SELECT_ENTITY,
    payload: { entityType, ...entity },
  });
}

export function selectEntityAndFlowId(entity, flowId, entityType = 'source') {
  store.dispatch({
    type: ACTIONS.SELECT_FLOW,
    payload: flowId,
  });
  store.dispatch({
    type: ACTIONS.SELECT_ENTITY,
    payload: { entityType, ...entity },
  });
}

export function openCopyEntity(entity, oFlows, flow, entityType = 'source', connectionName = '') {
  setOpenWidget();
  const { entities, flows } = store.getState();

  if (entities.selectedEntity !== entity && flows.selectedFlow !== flow)
    selectEntityAndFlowId(entity, flow, entityType);

  connectionName && setConnectionName(connectionName)

  store.dispatch({
    type: ACTIONS.COPY_ENTITIES,
    payload: oFlows,
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'CopyEntity',
      breadcrumbs: [
        {
          onClick: backToFlows,
        },
        {
          onClick: () => clickFlow(flow),
        },
        `Reuse ${entity.label}`,
      ],
    },
  });
}

export function showCopyEntity(entity, oFlows, entityType = 'source') {
  const { flows } = store.getState();
  const targetFlow = Selectors.supportedFlow(flows.selectedFlow);

  store.dispatch({
    type: ACTIONS.SELECT_ENTITY,
    payload: { entityType, ...entity },
  });

  store.dispatch({
    type: ACTIONS.COPY_ENTITIES,
    payload: oFlows,
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'CopyEntity',
      title: `Use the same ${entity.label} account?`,
      breadcrumbs: [
        {
          label: 'Flows',
          onClick: backToFlows,
        },
        {
          label: targetFlow.name,
          onClick: () => clickFlow(flows.selectedFlow),
        },
        `Reuse ${entity.label}`,
      ],
    },
  });
}

export function showSchedule(entity, entityType = 'source') {
  const { flows } = store.getState();
  const targetFlow = Selectors.supportedFlow(flows.selectedFlow);

  // Open the widget
  setOpenWidget();

  store.dispatch({
    type: ACTIONS.SELECT_ENTITY,
    payload: { entityType, ...entity },
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'SyncSchedule',
      title: `Update ${entity.label} schedule`,
      breadcrumbs: [
        {
          label: 'Flows',
          onClick: backToFlows,
        },
        {
          label: targetFlow.name,
          onClick: () => clickFlow(flows.selectedFlow),
        },
        {
          label: entity.label,
          onClick: () => goToEntityConfig(entity, entityType),
        },
        `Update ${entity.label} schedule`,
      ],
    },
  });
}
function selectSourceOrTarget(entity, entityType = 'source') {
  if (entityType === 'target')
    store.dispatch({
      type: ACTIONS.SELECT_TARGET,
      payload: entity,
    });

  if (entityType === 'source')
    store.dispatch({
      type: ACTIONS.SELECT_SOURCE,
      payload: entity,
    });
}

export function showSettingsConnectParams(entity, entityType = 'source') {
  const { flows } = store.getState();
  const targetFlow = Selectors.supportedFlow(flows.selectedFlow);

  selectSourceOrTarget(entity, entityType);
  // Open the widget
  setOpenWidget();

  store.dispatch({
    type: ACTIONS.SELECT_ENTITY,
    payload: { entityType, ...entity },
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'ConnectParams',
      title: `Configure ${entity.label}`,
      breadcrumbs: [
        {
          label: 'Flows',
          onClick: backToFlows,
        },
        {
          label: targetFlow.name,
          onClick: () => {
            clickFlow(flows.selectedFlow);
            selectSourceOrTarget(undefined, entityType);
          },
        },
        `Configure ${entity.label}`,
      ],
    },
  });
}

export function openEntityConfig(entity, entityType, flow, operation = undefined) {
  setOpenWidget();

  store.dispatch({
    type: ACTIONS.SELECT_FLOW,
    payload: flow,
  });

  store.dispatch({
    type: ACTIONS.SELECT_ENTITY,
    payload: { entityType, ...entity },
  });

  store.dispatch({
    type: ACTIONS.SET_DIALOG_SIZE,
    payload: { width: '583px', height: '686px' },
  });

  if (operation) {
    store.dispatch({
      type: ACTIONS.SET_OPERATION,
      payload: operation,
    })
  }

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'EntityConfig',
      title: `Configure ${entity.label}`,
      breadcrumbs: [
        {
          onClick: backToFlows,
        },
        {
          onClick: () => {
            clickFlow(flow);
            store.dispatch({
              type: ACTIONS.SET_DIALOG_SIZE,
              payload: { width: '400px', height: '600px' },
            });
          },
        },
        `Configure ${entity.label}`,
      ],
    },
  });
}

export function setConnectionName(connectionName) {
  store.dispatch({
    type: ACTIONS.SET_CONNECTION_NAME,
    payload: connectionName,
  });
}

export function goToEntityConfig(entity, entityType = 'source', flowId = undefined, shouldGoToCredentials, shouldGoToTriggers, shouldTriggerSelectFile) {
  const { flows } = store.getState();
  const targetFlow = Selectors.supportedFlow(flowId || flows.selectedFlow);

  if(shouldGoToCredentials)
    setNextStep('credentials')
    
  if(shouldGoToTriggers)
    setNextStep('triggers')

  if(shouldTriggerSelectFile)
    setNextStep('selectFile')

  store.dispatch({
    type: ACTIONS.SET_DIALOG_SIZE,
    payload: { width: '583px', height: '686px' },
  });

  store.dispatch({
    type: ACTIONS.SELECT_ENTITY,
    payload: { entityType, ...entity },
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'EntityConfig',
      title: `Configure ${entity.label}`,
      breadcrumbs: [
        {
          label: 'Flows',
          onClick: backToFlows,
        },
        {
          label: targetFlow.name,
          onClick: () => {
            store.dispatch({
              type: ACTIONS.SET_DIALOG_SIZE,
              payload: { width: '400px', height: '600px' },
            });
            clickFlow(flows.selectedFlow);
          },
        },
        `Configure ${entity.label}`,
      ],
    },
  });
}
export function showConnectParams(entity, entityType = 'source', nextStep) {
  const { flows } = store.getState();
  const targetFlow = Selectors.supportedFlow(flows.selectedFlow);

  // Open the widget
  setOpenWidget();

  store.dispatch({
    type: ACTIONS.SELECT_ENTITY,
    payload: { entityType, ...entity },
  });

  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'ConnectParams',
      title: `Configure ${entity.label}`,
      breadcrumbs: [
        {
          label: 'Flows',
          onClick: backToFlows,
        },
        {
          label: targetFlow.name,
          onClick: () => clickFlow(flows.selectedFlow),
        },
        `Configure ${entity.label}`,
      ],
      nextStep,
    },
  });
}

export function backToFlows() {
  store.dispatch({
    type: ACTIONS.CHANGE_PAGE,
    payload: {
      page: 'AvailableFlows',
      title: 'Connect your data',
      breadcrumbs: [],
    },
  });
}

export function setOpenWidget(dialogSize = true) {
  // Alert listener if exists
  const listener = Selectors.getListener();
  if (listener && listener.onWidgetOpen) {
    listener.onWidgetOpen();
  }
  if (dialogSize) store.dispatch(setDialogSize(400, 600));
  store.dispatch({
    type: ACTIONS.OPEN_WIDGET,
  });
}

export function isWidgetOpen() {
  return store.getState().navigation.open;
}

export function setHelperText(helperText) {
  store.dispatch({
    type: ACTIONS.SET_HELPER_TEXT,
    payload: helperText,
  });
}

export function setOperation(operation) {
  store.dispatch({
    type: ACTIONS.SET_OPERATION,
    payload: operation,
  });
}

export function setBreadcrumbs(breadcrumbs = true) {
  store.dispatch({
    type: ACTIONS.SET_BREADCRUMBS,
    payload: breadcrumbs,
  });
}

export function setHideJobStatusBadge(hideJobStatusBadge) {
  store.dispatch({
    type: ACTIONS.SET_HIDE_JOB_STATUS_BADGE,
    payload: hideJobStatusBadge,
  });
}

export function setFilter(filter) {
  store.dispatch({
    type: ACTIONS.SET_FILTER,
    payload: filter,
  });
}

export function setCredentialsFormValidator(credentialsFormValidator) {
  store.dispatch({
    type: ACTIONS.SET_CREDENTIALS_FORM_VALIDATOR,
    payload: credentialsFormValidator,
  });
}

export function setListener(listener) {
  store.dispatch({
    type: ACTIONS.SET_LISTENER,
    payload: listener,
  });
}

export function setDemo(demo = false) {
  store.dispatch({
    type: ACTIONS.SET_DEMO,
    payload: demo,
  });
}

export function setJwtToken(jwtToken) {
  store.dispatch({
    type: ACTIONS.SET_TOKEN,
    payload: jwtToken,
  });
}

export function setLocalization(localization) {
  store.dispatch({
    type: ACTIONS.SET_LOCALIZATION,
    payload: localization,
  });
}

export function setSubTenantsEnabled(subTenantsEnabled) {
  store.dispatch({
    type: ACTIONS.SET_SUBTENANTS_ENABLED,
    payload: subTenantsEnabled,
  });
}
export function setShowSubTenantsId(showSubTenantsId) {
  store.dispatch({
    type: ACTIONS.SET_SHOW_SUB_TENANTS_ID,
    payload: showSubTenantsId,
  });
}

export function setMultipleSources(multipleSources) {
  store.dispatch({
    type: ACTIONS.SET_MULTIPLE_SOURCES,
    payload: multipleSources,
  });
}

export function addSchemaTable(flowId) {
  store.dispatch({
    type: ACTIONS.ADD_SCHEMA_TABLE,
    payload: flowId,
  });
}

export function loadMapping(mapping, flowId){
  store.dispatch({
    type: ACTIONS.LOAD_MAPPING,
    payload: mapping,
    params: { flowId }
  })
}

export function setMultipleTables(multipleTables) {
  store.dispatch({
    type: ACTIONS.SET_MULTIPLE_TABLES,
    payload: multipleTables,
  });
}

export function setEnforceOneToOneMapping(enforceOneToOneMapping) {
  store.dispatch({
    type: ACTIONS.SET_ENFORCE_ONE_TO_ONE_MAPPING,
    payload: enforceOneToOneMapping,
  });
}

export function setMultipleConnectors(multipleConnectors) {
  store.dispatch({
    type: ACTIONS.SET_MULTIPLE_CONNECTORS,
    payload: multipleConnectors,
  });
}

export function setNextStep(nextStep) {
  store.dispatch({
    type: ACTIONS.SET_NEXT_STEP,
    payload: nextStep,
  });
}

export function setShouldDisable(shouldDisable) {
  store.dispatch({
    type: ACTIONS.SET_SHOULD_DISABLE,
    payload: shouldDisable,
  });
}

export function setHideBackButtons(hideBackButtons) {
  store.dispatch({
    type: ACTIONS.SET_HIDE_BACK_BUTTONS,
    payload: hideBackButtons,
  });
}

export function startSourceLink() {
  store.dispatch({
    type: ACTIONS.START_LINKING_SOURCE,
  });
}

export function stopSourceLink() {
  store.dispatch({
    type: ACTIONS.STOP_LINKING_SOURCE,
  });
}

export async function preload(tenant, flow) {
  // Set the tenant
  store.dispatch({
    type: ACTIONS.SET_TENANT,
    payload: tenant,
  });

  // Set the flow
  store.dispatch({
    type: ACTIONS.SELECT_FLOW,
    payload: flow,
  });

  const flowVersion = store.getState().flows?.supportedFlows?.[flow]?.version ?? 1;
  // Get linked flows
  const { payload: linked } =
    flowVersion === 2
      ? await store.dispatch(NetworkActions.fetchLinkedConnectors())
      : await store.dispatch(NetworkActions.fetchLinkedSources());

  // Get supported sources for flow if not linked
  if (!linked || linked.length === 0) {
    flowVersion === 2
      ? store.dispatch(NetworkActions.fetchSupportedConnectors(flow))
      : store.dispatch(NetworkActions.fetchSupportedSources(flow));
  }
}
export async function disconnect(tenant, flow, entityId, options = {}) {
  handleOptions(options);
  const { isTarget = false, isConnector = false } = options;
  const listener = Selectors.getListener();
  if (isTarget) {
    await API.disconnectLinkedTarget(entityId, flow, tenant);
    if (listener && listener.onTargetUnlinked) {
      listener.onTargetUnlinked(entityId, flow);
    }
  } else if(!isConnector) {
    await API.disconnectLinkedSource(entityId, flow, tenant);
    // Alert listener if exists
    if (listener && listener.onSourceUnlinked) {
      listener.onSourceUnlinked(entityId, flow);
    }
  } else if(isConnector){
    await API.disconnectLinkedConnector(entityId, flow, tenant)
    if (listener && listener.onConnectorUnlinked) {
      listener.onConnectorUnlinked(entityId, flow);
    }
  }

}

export async function reconnect(tenant, flow, entityId, options = {}) {
  const state = store.getState();
  const { isTarget = false } = options;
  const flowVersion = store.getState().flows?.supportedFlows ? store.getState().flows.supportedFlows[flow]?.version : 1;
  const lookupKey = isTarget ? 'targets' : flowVersion === 2 ? 'connectors' : 'sources';

  if (state.configuration.tenant_id !== tenant) {
    await store.dispatch({
      type: ACTIONS.SET_TENANT,
      payload: tenant,
    });
  }

  if (state.flows.selectedFlow !== flow) {
    await store.dispatch({
      type: ACTIONS.SELECT_FLOW,
      payload: flow,
    });
  }

  if (isTarget) await store.dispatch(NetworkActions.fetchLinkedTargets());
  else if (lookupKey === 'sources') await store.dispatch(NetworkActions.fetchLinkedSources());
  else await store.dispatch(NetworkActions.fetchLinkedConnectors());

  const linkedEntity = Object.values(store.getState()[lookupKey].linked).find((x) => {
    if (!isTarget && lookupKey === 'sources') return x.tap === entityId;
    else if (!isTarget && lookupKey === 'connectors') return x.id = entityId;
    else return x.target === entityId;
  });

  if (!linkedEntity) return;
  const { type, label, demo, connection_name } = linkedEntity;
  const reconnectEnabled = !demo && type === 'oauth';

  if (!reconnectEnabled) return;

  if (lookupKey === 'sources') {
    await store.dispatch(NetworkActions.doUpdateLinkedSourceConfig(entityId, { isReconnecting: true }));
  } else if (lookupKey === 'connectors') {
    await store.dispatch(NetworkActions.doUpdateLinkedConnectorConfig(entityId, { isReconnecting: true }));
  }

  if (!isTarget) {
    if (lookupKey === 'sources') {
      const tapUrl = await SourceCardUtil.getTapUrl(entityId, undefined, flow, tenant, connection_name);
      if (!tapUrl) return;
      SourceCardUtil.createAndPollLink(tapUrl, label, entityId, undefined, undefined, true);
    } else {
      const authUrl = await ConnectorCardUtil.getAuthUrl(entityId, undefined, flow, tenant, connection_name);
      if (!authUrl) return;
      ConnectorCardUtil.createAndPollLink(authUrl, label, entityId, tenant, undefined, true);
    }
  } else {
    const target_url = await TargetCardUtil.getTargetUrl(entityId, undefined, flow, tenant);
    if (!target_url) return;
    TargetCardUtil.createAndPollLink(target_url, label, entityId);
  }

  const listener = Selectors.getListener();
  if (listener && listener.onReconnect) {
    listener.onReconnect(entityId, flow);
  }
}

export async function setAPollingJob(jobRoot, flow, tenant, value) {
  return store.dispatch({
    type: ACTIONS.SET_POLLING_JOB,
    payload: value,
    params: { jobRoot, flow, tenant },
  });
}

export function setDialogSize(width, height) {
  return {
    type: ACTIONS.SET_DIALOG_SIZE,
    payload: { width, height },
  };
}

export function setWidth(width) {
  return {
    type: ACTIONS.SET_WIDTH,
    payload: width,
  };
}

export function setConnectParams(connectParams) {
  store.dispatch({
    type: ACTIONS.SET_CONNECT_PARAMS,
    payload: connectParams,
  });
}

export function setHideFlows(hideFlows) {
  return {
    type: ACTIONS.SET_HIDE_FLOWS,
    payload: hideFlows,
  };
}

export function setHeight(height) {
  return {
    type: ACTIONS.SET_HEIGHT,
    payload: height,
  };
}
export function setIsLinking(isLinking) {
  store.dispatch({
    type: ACTIONS.SET_IS_LINKING,
    payload: isLinking,
  });
}
export async function link(tenant, flow, id, preloaded, options) {
  // Clear all alerts before opening
  // soft validation for tenant being a string here
  tenant = String(tenant)
  if (/\s/.test(tenant)) {
    throw new Error('hotglue tenant ids cannot have spaces!');
  }
  PopupAlertManager.clearModal();
  // Handle options
  const { connectionName, connectValues, isTarget = false, multipleSources = false, multipleConnectors = false, isConnection = false, nextStep = store.getState().configuration?.nextStep, checkLink = true, ...rest } = options;
  const flowVersion = store.getState().flows?.supportedFlows ? store.getState().flows.supportedFlows[flow]?.version : 1;
  const lookupKey = isTarget ? 'targets' : flowVersion === 2 ? 'connectors' : 'sources';

  if (!store.getState().flows.supportedFlows) await store.dispatch(NetworkActions.fetchSupportedFlow(flow));
  if (!store.getState().preferences.featureFlags) await store.dispatch(NetworkActions.fetchFeatureFlags());

  setConnectionName(connectionName);
  handleOptions({ flow, multipleSources, multipleConnectors, nextStep, tenantId: tenant, ...rest });
  // Directly try to link, or merely open the flow page if it's already linked
  if (!preloaded || store.getState().configuration.tenant_id !== tenant) {
    // Set the tenant
    if (!isConnection)
      store.dispatch({
        type: ACTIONS.SET_TENANT,
        payload: tenant,
      });

    // Set the flow
    store.dispatch({
      type: ACTIONS.SELECT_FLOW,
      payload: flow,
    });
  }
  const multipleTenantLinks = store.getState().preferences.multipleTenantLinks;
  const subtenantDelimiter = store.getState().preferences.featureFlags?.subtenantDelimiter ?? '_';

  // Get the linked items
  if ((!preloaded || !store.getState()[lookupKey].linked) && !multipleTenantLinks && checkLink)
    if (isTarget) await store.dispatch(NetworkActions.fetchLinkedTargets());
    else if (flowVersion === 2)
      await store.dispatch(NetworkActions.fetchLinkedConnectors(undefined, undefined, tenant));
    else await store.dispatch(NetworkActions.fetchLinkedSources(tenant));

  if (multipleTenantLinks && checkLink) {
    // NOTE: we used to do a tenant.split('_')[0] here, not sure why so I have removed
    // goal was to make syncro's HotGlue.link call load faster (Hassan)
    if (flowVersion === 2)
      await store.dispatch(NetworkActions.fetchLinkedConnectorByFlowAndTenant(id, flow, tenant));
    else if (!isTarget && checkLink)
      await store.dispatch(NetworkActions.fetchLinkedSourceByFlowAndTenant(id, flow, tenant));
    else if (checkLink) await store.dispatch(NetworkActions.fetchLinkedTargets());
  }

  checkLink && await store.dispatch(NetworkActions.fetchLinkedFlows());

  const linked = Object.keys(store.getState()[lookupKey].linked || {});
  const linkedEntity = Object.values(store.getState()[lookupKey].linked || [{}])[0];
  const linkedList = store.getState()[lookupKey].linkedList || [{}];
  if (
    (!linked || linked.length === 0 || tenant.includes(subtenantDelimiter) || multipleSources || multipleConnectors) &&
    (!linked?.includes(id) || (linkedEntity?.tenant_id !== tenant && !linkedList.some((x) => x.tenant_id === tenant)))
  ) {
    // Get the supported source
    if ((!store.getState()[lookupKey].supported || !store.getState()[lookupKey].supported[flow])) {
      if (isTarget) await store.dispatch(NetworkActions.fetchSupportedTargets(flow));
      else if (flowVersion === 2) await store.dispatch(NetworkActions.fetchSupportedConnectors(flow));
      else await store.dispatch(NetworkActions.fetchSupportedSources(flow));
    }

    const entity = store.getState()[lookupKey].supported[flow][id];

    store.dispatch({
      type: ACTIONS.SELECT_ENTITY,
      payload: entity,
    });

    const listener = Selectors.getListener();

    if (!entity) {
      const errorMessage = `Error! Flow ${flow} has no ${id} ${isTarget ? 'target' : 'source'} configured.`;
      console.error(errorMessage);

      if (listener && listener.onError) {
        listener.onError(errorMessage, {
          flowId: flow,
          entityId: id,
          isTarget,
        });
      }
      return;
    }

    // Link the entity
    try {
      if (isTarget) await TargetCardUtil.doLink(entity, connectValues);
      else if (flowVersion === 2) await ConnectorCardUtil.doLink({ ...entity, connection_name: connectionName }, tenant, nextStep, connectValues, checkLink);
      else await SourceCardUtil.doLink({ ...entity, connection_name: connectionName }, tenant, nextStep, connectValues, checkLink);
    } catch (error) {
      if (isTarget && listener?.onTargetLinkFailed) {
        listener.onTargetLinkFailed(entity, flow, tenant, error?.message ?? String(error));
      } else if (flowVersion === 2 && listener?.onConnectorLinkFailed) {
        listener.onConnectorLinkFailed(entity, flow, tenant, error?.message ?? String(error));
      } else if (listener?.onSourceLinkFailed) {
        listener.onSourceLinkFailed(entity, flow, tenant, error?.message ?? String(error));
      } else {
        throw error;
      }
    }
  } else if (multipleSources || multipleConnectors || linked?.includes(id)) {
    let entity = store.getState()[lookupKey].linked[id];

    // Pick the correct entity from the linkedList
    if (entity.tenant_id !== tenant && multipleTenantLinks) {
      entity = linkedList.find(x => x.tenant_id === tenant)
      if (!entity && !tenant.includes(subtenantDelimiter)) {
        entity = linkedList.find(x => x.tenant_id.startsWith(tenant))
      }
    }

    // Open entity config
    goToEntityConfig(entity, lookupKey.slice(0, -1));

    // Open the widget
    setOpenWidget(false);
  } else {
    const targetFlow = Selectors.supportedFlow(flow);

    // Set the page to flow page
    store.dispatch({
      type: ACTIONS.CHANGE_PAGE,
      payload: {
        page: isTarget ? 'FlowTargets' : flowVersion === 2 ? 'FlowV2Connectors' : 'FlowSources',
        title: `Where is your ${targetFlow.name} data?`,
        breadcrumbs: [
          {
            label: 'Flows',
            onClick: backToFlows,
          },
          targetFlow.name,
        ],
      },
    });

    // Open the widget
    setOpenWidget();
  }
}

export const setMappingSchema = (schemas) => {
  schemas.forEach((schema) => {
    // TODO: add validation for the properties table, fields, fieldObjects.
    store.dispatch({
      type: ACTIONS.SET_SCHEMA,
      payload: schema,
    });
  });
};

export const setMetadata = (metadata, tenantId) => {
  return API.updateTenantMetadata(metadata, undefined, tenantId);
};

export const openGoogleWidget = async (entity, tenant, flowId) => {
  const { app_id: googleAppId, api_key: googleApiKey } = entity.google_tokens;

  if (!googleAppId || !googleApiKey) {
    throw new Error('Google Drive App ID, API key, and access token are required');
  }
  let accessToken = entity.config.access_token;
  try {
    // ensure that the access token is valid and refresh it if needed
    const response = await API.getAccessToken(entity.id ?? entity.tap, entity.entityType, flowId, tenant);
    accessToken = response.access_token;
  } catch (error) {
    console.error('Failed to fetch the access token for Google Picker widget:', error, {
      entityId: entity.id ?? entity.tap,
      entityType: entity.entityType,
      flowId,
      tenant,
    });
  }
  const currentNavigation = store.getState().navigation;
  store.dispatch({
    type: ACTIONS.CLOSE_WIDGET,
  });

  // Load Google APIs if not already loaded
  const loadGoogleAPIs = async () => {
    return new Promise((resolve, reject) => {
      if (window.google?.picker) {
        console.log('Google Picker API already loaded');
        resolve(window.google);
        return;
      }
      // Helper function to load the Picker API
      const loadPickerApi = () => {
        window.gapi.load('picker', {
          callback: () => {
            console.log('Google Picker API loaded, window.google.picker:', window.google?.picker);
            if (window.google?.picker) {
              resolve(window.google);
            } else {
              reject(new Error('Google Picker API failed to load properly'));
            }
          },
          onerror: (error) => {
            console.error('Error loading Google Picker API:', error);
            reject(new Error('Failed to load Google Picker API'));
          },
        });
      };

      // Check if gapi is already defined before loading the script
      if (window.gapi && window.gapi.load) {
        // gapi is already loaded, just load the picker
        loadPickerApi();
      } else {
        // gapi is not loaded, load the script
        const script1 = document.createElement('script');
        script1.src = 'https://apis.google.com/js/api.js';
        script1.onload = () => {
          console.log('Google APIs script loaded, window.google:', window.google);

          // Now load the Picker API using gapi.load
          if (window.gapi) {
            loadPickerApi();
          } else {
            reject(new Error('gapi not available after script load'));
          }
        };
        script1.onerror = (error) => {
          console.error('Error loading Google APIs script:', error);
          reject(new Error('Failed to load Google APIs script'));
        };
        document.head.appendChild(script1);
      }
    });
  };

  // Create and display the Google Picker
  const createPicker = async () => {
    try {
      // Load Google APIs
      await loadGoogleAPIs();

      // Wait for Google APIs to be available
      if (!window.google?.picker) {
        throw new Error('Google Picker API failed to load');
      }

      createPickerWithToken();
    } catch (error) {
      console.error('Error creating Google Picker:', error);
      // Show user-friendly error message
      PopupAlertManager.Alert({
        type: 'error',
        text: `Failed to initialize Google Picker: ${error.message}`,
      });
    }
  };

  const createPickerWithToken = () => {
    try {
      // Create the picker builder following Google's documentation
      console.log('Creating Google Picker with:', {
        accessToken: accessToken,
        googleApiKey: googleApiKey,
        googleAppId: googleAppId,
        entityId: entity.id ?? entity.tap,
      });

      const pickerBuilder = new window.google.picker.PickerBuilder()
        .setOAuthToken(accessToken)
        .setDeveloperKey(googleApiKey)
        .setCallback(pickerCallback)
        .setAppId(googleAppId)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES);

      // Add appropriate view based on connector type
      if ((entity.id ?? entity.tap).includes('sheet')) {
        // For Google Sheets, show spreadsheets view
        pickerBuilder.addView(window.google.picker.ViewId.SPREADSHEETS);
      } else {
        // For Google Drive, show documents view
        pickerBuilder.addView(window.google.picker.ViewId.DOCS);
      }

      // Build and show the picker
      const picker = pickerBuilder.build();
      picker.setVisible(true);

      console.log('Google Picker created and set visible');
    } catch (error) {
      console.error('Error building Google Picker:', error);
      // Show user-friendly error message
      PopupAlertManager.Alert({
        type: 'error',
        text: `Failed to open Google Picker: ${error.message}`,
      });
    }
  };

  // Callback function to handle picker responses (as per Google documentation)
  const pickerCallback = async (data) => {
    console.log('Google Picker callback data:', data);

    if (data[window.google.picker.Response.ACTION] === window.google.picker.Action.PICKED) {
      const documents = data[window.google.picker.Response.DOCUMENTS];
      const selectedFiles = documents.map((document) => ({ id: document.id, name: document.name }));
      console.log('::selectedFiles', selectedFiles);

      try {
        await API.updateSelectedFiles(selectedFiles, entity.id ?? entity.tap, flowId, tenant);
        store.dispatch({
          type: ACTIONS.CHANGE_PAGE,
          payload: currentNavigation,
        });
        PopupAlertManager.Alert({ type: 'success', text: `Selected ${selectedFiles.length} files successfully!` });
      } catch (error) {
        console.error('Error updating selected files:', error);
        PopupAlertManager.Alert({
          type: 'error',
          text: `Failed to update selected files: ${error.message}`,
        });
      }
    } else if (data[window.google.picker.Response.ACTION] === window.google.picker.Action.CANCEL) {
      console.log('User canceled Google Drive Picker');
    } else {
      // Handle other actions or errors
      console.log('Google Picker action:', data[window.google.picker.Response.ACTION]);
      if (data[window.google.picker.Response.ERROR]) {
        console.error('Google Picker error:', data[window.google.picker.Response.ERROR]);
        PopupAlertManager.Alert({
          type: 'error',
          text: `Google Picker error: ${data[window.google.picker.Response.ERROR]}`,
        });
      }
    }
  };

  // Start the process
  await createPicker();
};

export async function handleOptions(options = {}) {
  const {
    flow,
    demo,
    filter,
    jwtToken,
    breadcrumbs,
    listener,
    helperText,
    localization,
    multipleSources,
    multipleConnectors,
    multipleTables,
    enforceOneToOneMapping,
    hideBackButtons,
    showSubTenantsId,
    subTenantsEnabled,
    schemas,
    tenantMetadata,
    connectParams,
    connectionName,
    nextStep,
    tenantId,
    hideJobStatusBadge,
    credentialsFormValidator,
  } = options;

  if (flow) {
    // TODO: Allow passing flow name instead of id
    clickFlow(flow);
  } else {
    // If there is no flow need to reset it
    backToFlows();
  }

  // Even if there is no listener or filter, it is safe to pass undefined
  setFilter(filter);
  if (!isEmptyObject(listener)) setListener(listener);
  setDemo(demo);
  if (jwtToken !== undefined) setJwtToken(jwtToken);
  if (helperText !== undefined) setHelperText(helperText);
  if (showSubTenantsId !== undefined) setShowSubTenantsId(showSubTenantsId)
  if (subTenantsEnabled !== undefined) setSubTenantsEnabled(subTenantsEnabled)
  if (localization !== undefined) setLocalization(localization);
  if (multipleTables !== undefined) setMultipleTables(multipleTables)
  if (enforceOneToOneMapping !== undefined) setEnforceOneToOneMapping(enforceOneToOneMapping)
  if (multipleSources !== undefined) setMultipleSources(multipleSources);
  if (multipleConnectors !== undefined) setMultipleConnectors(multipleConnectors);
  if (hideBackButtons !== undefined) setHideBackButtons(hideBackButtons || !!connectionName);
  if (connectParams !== undefined) setConnectParams(connectParams);
  if (credentialsFormValidator !== undefined) setCredentialsFormValidator(credentialsFormValidator);
  schemas && setMappingSchema(schemas);
  tenantMetadata && setMetadata(tenantMetadata, tenantId);
  if (breadcrumbs !== undefined) setBreadcrumbs(breadcrumbs);
  if (nextStep !== undefined) setNextStep(nextStep)
  if (hideJobStatusBadge !== undefined) setHideJobStatusBadge(hideJobStatusBadge)
}
